import { USER_ROLES } from "../../Utility/constants";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  StyledEngineProvider,
  Typography,
} from "@mui/material";
import {
  fetchPrintSections,
  fetchPrintSurveyMenu,
  fetchBuilderAdminPrintSurveyMenu,
  fetchSectionData,
  resetSectionsData,
  setSectionData,
  setTotalSectionData, checkLoginStatus, setBuilderLogin,
} from "../Survey/Survey.slice";
import RenderRadioGrp from "../../components/QuestionTypes/RenderRadioGrp";
import RenderCheckboxGrp from "../../components/QuestionTypes/RenderCheckboxGrp";
import RenderCommonGrp from "../../components/QuestionTypes/RenderCommonGrp";
import RenderQuestion from "../../components/QuestionTypes/RenderQuestion";
import { getTextFromHTML, htmlParser } from "../../Utility/constants";
import HelpTextPopper from "../../components/Common/Popper";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import "../Survey/SurveyForm.scss";
import { getSurveyLookupFiling } from "../Survey/Survey.service";

const PrintSurvey = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const componentRef = useRef(null);

  const location = useLocation();
  const { surveyFilingId, fromBuilder } = location.state || {};
  const [searchParams] = useSearchParams();

  // Check for URL parameters for auto-render mode (used by Gotenberg/headless rendering)
  const urlFilingId = searchParams.get('filingId');
  const urlRespondentId = searchParams.get('respondentId');
  const urlSurveyId = searchParams.get('surveyId');
  const urlHospitalName = searchParams.get('hospitalName');
  const urlAhaId = searchParams.get('ahaId');
  const urlAuthHeader = searchParams.get('authHeader');
  const urlCookieValue = searchParams.get('cookieValue');
  const urlApiBaseUrl = searchParams.get('apiBaseUrl');
  const isAutoRender = Boolean(urlFilingId && urlRespondentId && urlSurveyId);

  // Store auth credentials and API base URL override in sessionStorage for API calls
  useEffect(() => {
    if (isAutoRender && urlAuthHeader && urlCookieValue) {
      sessionStorage.setItem('pdfAuthHeader', urlAuthHeader);
      sessionStorage.setItem('pdfCookieValue', urlCookieValue);
      console.log('Auto-render: Stored auth credentials in sessionStorage');
    }
    if (isAutoRender && urlApiBaseUrl) {
      sessionStorage.setItem('apiBaseUrl', urlApiBaseUrl);
      console.log('Auto-render: Stored API base URL override in sessionStorage:', urlApiBaseUrl);
    }
    if (isAutoRender && urlFilingId) {
      sessionStorage.setItem('pdfFilingId', urlFilingId);
    }
  }, [isAutoRender, urlAuthHeader, urlCookieValue, urlApiBaseUrl, urlFilingId]);

  const sectionsData = useSelector((state) => state?.survey?.printSecFilings);
  const surveyRespondentId = useSelector(
    (state) => state.survey.respondentDetails?.id
  );
  const surveyFiling = useSelector(
    (state) => state.survey.surveyFiling[0]
  );
  const surveyName = useSelector(
    (state) => state.survey.activeSurveys[0]?.name
  );
  const reduxHospitalName = useSelector(
    (state) => state.survey.respondentDetails?.surveyOrganization?.name
  );
  const reduxAhaId = useSelector(
    (state) => state.survey.respondentDetails?.surveyOrganization?.customId
  );

  // Use URL params in auto-render mode, otherwise use Redux state
  const HospitalName = isAutoRender ? urlHospitalName : reduxHospitalName;
  const ahaId = isAutoRender ? urlAhaId : reduxAhaId;
  const { state } = useLocation();
  const sectionsPageData = useSelector((state) => state.survey?.sectionsData);
  const [surveyData, setSurveyData] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionSelection, setSectionSelection] = useState(null);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [surveyId, setSurveyId] = useState(null);
  const [selectedSurFilId, setSelectedSurFilId] = useState(null);
  const surveyMenu = useSelector((state) => state.survey.printSurveyMenu);
  const isbuilderLogin = useSelector((state) => state?.survey?.isbuilderLogin);
  const [allSectionsLoaded, setAllSectionsLoaded] = useState(false);
  const hasSetTimeout = useRef(false);
  const startTime = useRef(Date.now());
  const lastGateKey = useRef(null);


  useEffect(() => {
    if (isAutoRender) {
      // Auto-render mode: fetch data using URL parameters
      dispatch(setBuilderLogin(true));
      dispatch(fetchBuilderAdminPrintSurveyMenu({ surveyFilingId: urlFilingId }));
      dispatch(fetchPrintSections({ surveyFilingId: urlFilingId, surveyRespondentId: urlRespondentId }));
    } else if (fromBuilder && surveyFilingId) { // this is coming directly from the Process Survey page in the builder
      dispatch(setBuilderLogin(true));
      dispatch(fetchBuilderAdminPrintSurveyMenu({surveyFilingId: surveyFilingId}));
    } else if (isbuilderLogin && surveyFiling?.id) { // this is coming to the print page after using the View or Edit options on the Process Survey page in the builder
      dispatch(fetchBuilderAdminPrintSurveyMenu({ surveyFilingId: surveyFiling?.id }));
    } else {
      dispatch(fetchPrintSurveyMenu({ surveyFilingId: surveyFiling?.id, surveyRespondentId }));
    }
  }, [dispatch, isAutoRender, urlFilingId, urlRespondentId]);

  useEffect(() => {
    let entireSurveyObj = {
      label: "Print Entire Survey",
      value: "totalSurvey",
    };

    // Filter out sections with "Page Skipped" status
    const visibleSections = (sectionsData || []).filter((item) => {
       return !(item.status && item.status.name === "Page Skipped");
    });

    let options = [
      entireSurveyObj,
      ...visibleSections.map((item) => ({
        label: "Print Section " + item?.section?.name,
        value: item?.section?.id,
      })),
    ];
    setSectionOptions(options);
  }, [sectionsData]);

  // Ship diagnostics to the taker so they land in CloudWatch. Gotenberg's headless
  // browser has no devtools and its console output is not captured anywhere.
  // Deliberately uses a bare fetch rather than api.service so it still reports
  // when the normal request path is the thing that is broken.
  const reportDiag = (event, detail) => {
    const body = {
      event,
      filingId: urlFilingId,
      respondentId: urlRespondentId,
      elapsedSec: ((Date.now() - startTime.current) / 1000).toFixed(2),
      apiBaseUrlInSession: sessionStorage.getItem('apiBaseUrl'),
      buildTimeBaseUrl: process.env.REACT_APP_BASE_URL,
      hasPdfAuthHeader: Boolean(sessionStorage.getItem('pdfAuthHeader')),
      pageOrigin: window.location.origin,
      ...detail,
    };
    console.log(`[DIAG] ${event}`, body);
    try {
      const base = sessionStorage.getItem('apiBaseUrl') || process.env.REACT_APP_BASE_URL;
      fetch(`${base}/api/v1/taker/print-diagnostics`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch((e) => console.error('[DIAG] failed to report:', e?.message));
    } catch (e) {
      console.error('[DIAG] failed to report:', e?.message);
    }
  };

  // Auto-render mode: automatically load all section data when sections are available
  useEffect(() => {
    if (isAutoRender && sectionsData?.length > 0 && !sectionSelection) {
      const elapsed = ((Date.now() - startTime.current) / 1000).toFixed(2);
      console.log(`[${elapsed}s] Auto-render: Setting up to load all sections`);

      // Set the necessary state variables
      setSurveyId(parseInt(urlSurveyId));
      setSelectedSurFilId(parseInt(urlFilingId));
      setSectionSelection("totalSurvey");
      setSectionsLoading(true);

      // Fetch all section data using Promise.all to wait for all to complete
      const fetchAllSections = async () => {
        // Filter out sections with "Page Skipped" status
        const visibleSections = sectionsData.filter((sec) => {
          return !(sec.status && sec.status.name === "Page Skipped");
        });

        reportDiag('fetch-start', {
          sectionsDataLength: sectionsData.length,
          visibleCount: visibleSections.length,
          sectionIds: visibleSections.map((sec) => sec?.sectionId),
        });

        const outcomes = [];

        const promises = visibleSections.map((sec) => {
          let payload = {
            surveyId: parseInt(urlSurveyId),
            surveyFilingId: parseInt(urlFilingId),
            sectionId: sec?.sectionId,
            surveyRespondentId: parseInt(urlRespondentId),
          };
          console.log(`[DIAG] dispatching fetchSectionData for section ${sec?.sectionId}`, payload);
          return dispatch(fetchSectionData(payload))
            .then((data) => {
              const status = data?.meta?.requestStatus;
              if (status === "rejected") {
                reportDiag('section-rejected', {
                  sectionId: sec?.sectionId,
                  errorName: data?.error?.name,
                  errorMessage: data?.error?.message,
                  errorCode: data?.error?.code,
                });
                outcomes.push({ sectionId: sec?.sectionId, status: "rejected" });
              } else if (!data?.payload) {
                reportDiag('section-empty-payload', {
                  sectionId: sec?.sectionId,
                  payloadType: typeof data?.payload,
                  payloadIsNull: data?.payload === null,
                  requestStatus: status,
                  hasError: Boolean(data?.error),
                  errorName: data?.error?.name,
                  errorMessage: data?.error?.message,
                });
                outcomes.push({ sectionId: sec?.sectionId, status: "empty-payload" });
              } else {
                outcomes.push({ sectionId: sec?.sectionId, status: "ok" });
              }
              if (data?.payload) {
                dispatch(setTotalSectionData(data.payload));
              }
              return data;
            })
            .catch((err) => {
              reportDiag('section-threw', {
                sectionId: sec?.sectionId,
                errorMessage: err?.message,
                errorName: err?.name,
              });
              outcomes.push({ sectionId: sec?.sectionId, status: "threw" });
              return null;
            });
        });

        await Promise.all(promises);
        const elapsed = ((Date.now() - startTime.current) / 1000).toFixed(2);
        console.log(`[${elapsed}s] Auto-render: All section data fetched`);
        reportDiag('fetch-complete', {
          requested: visibleSections.length,
          ok: outcomes.filter((o) => o.status === "ok").length,
          failed: outcomes.filter((o) => o.status !== "ok").length,
          outcomes,
        });
      };

      fetchAllSections();
    }
  }, [isAutoRender, sectionsData, sectionSelection, urlSurveyId, urlFilingId, urlRespondentId, dispatch]);

  useEffect(() => {
        // Wait for ALL sections to load before showing marker
        // Filter out sections with "Page Skipped" status to get accurate count
        const visibleSectionsCount = sectionsData?.filter((sec) => {
          return !(sec.status && sec.status.name === "Page Skipped");
        }).length || 0;

        // Report only on change; this effect re-runs on every render.
        const gateKey = `${visibleSectionsCount}|${sectionsPageData?.length}|${sectionSelection}`;
        if (isAutoRender && lastGateKey.current !== gateKey) {
          lastGateKey.current = gateKey;
          reportDiag('gate-check', {
            visibleSectionsCount,
            accumulatedCount: sectionsPageData?.length,
            accumulatedIds: (sectionsPageData || []).map((s) => s?.id),
            sectionSelection,
            hasSetTimeout: hasSetTimeout.current,
            gatePasses: visibleSectionsCount === sectionsPageData?.length,
          });
        }

        if (
            sectionsData?.length > 0 &&
            visibleSectionsCount === sectionsPageData?.length &&
            sectionSelection === "totalSurvey" &&
            !hasSetTimeout.current  // Only set timeout once
        ) {
            const elapsed = ((Date.now() - startTime.current) / 1000).toFixed(2);
            console.log(`[${elapsed}s] Auto-render: All ${sectionsPageData.length} sections loaded, starting render delay`);

            setSurveyData(sectionsPageData);
            setSectionsLoading(false);
            hasSetTimeout.current = true;

            // Allow time for React to render all data to DOM
            setTimeout(() => {
                const finalElapsed = ((Date.now() - startTime.current) / 1000).toFixed(2);
                console.log(`[${finalElapsed}s] Auto-render: Setting allSectionsLoaded to true - MARKER WILL NOW APPEAR`);
                setAllSectionsLoaded(true);
            }, 15000);  // 15 second render delay - reduced now that CSS fixes allow proper printing
        } else {
            setSurveyData(sectionsPageData);
            setSectionsLoading(false);
            if (!hasSetTimeout.current) {
                setAllSectionsLoaded(false);  // Only reset if we haven't set the timeout yet
            }
        }
    }, [sectionsPageData, sectionsData, sectionSelection]);

    const handleBack = () => {
    navigate(`/`, { state: { showFilingList: false } });
  };

  const reactToPrintContent = useCallback(() => {
    return componentRef.current;
  }, [componentRef.current]);

  const getPDFName = () => {
    let name = `${getTextFromHTML(
      surveyMenu?.surveys?.find((survey) => survey.id === surveyId)?.name
    )}-${HospitalName}`;
    return name.replace(/[.'"]/g, "");
  };

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: getPDFName(),
    pageStyle: `
      @page {
        size: auto;
        margin: 11mm 17mm 17mm 17mm;

        @top-right {
            content: "Page " counter(page);
          }
      }
        .page-break {
          display: block;
          page-break-before: always;
        }
      }
    `,
    removeAfterPrint: true,
  });

  const handleSurveyToPrint = async (event) => {
    setSurveyId(event?.target?.value);
    if (event?.target?.value) {
      const response = await getSurveyLookupFiling({
        surveyId: event?.target?.value,
        surveyRespondentId,
      });

      setSelectedSurFilId(response?.data[0]?.id);

      const payload = {
        surveyFilingId: response?.data[0]?.id,
        surveyRespondentId: surveyRespondentId,
      };
      dispatch(fetchPrintSections(payload));
    }
    dispatch(resetSectionsData());
    setSectionSelection(null);
  };

  const handleSectionSelection = async (event) => {
    setSectionSelection(event?.target?.value);
    setSectionsLoading(true);
    setAllSectionsLoaded(false);
    if (event?.target?.value === "totalSurvey") {
        // Filter out sections with "Page Skipped" status
        const visibleSections = sectionsData.filter((sec) => {
            return !(sec.status && sec.status.name === "Page Skipped");
        });

      visibleSections.forEach(async (sec) => {
        let payload = {
          surveyId: surveyId,
          surveyFilingId: selectedSurFilId,
          sectionId: sec?.sectionId,
          surveyRespondentId: surveyRespondentId,
        };
        await dispatch(fetchSectionData(payload)).then((data) => {
          // Combine or merge the section data as needed
          // For simplicity, this example assumes you replace existing data

          dispatch(setTotalSectionData(data?.payload));
        });
      });
    } else if (event?.target?.value) {
      let payload = {
        surveyId: surveyId,
        surveyFilingId: selectedSurFilId,
        sectionId: event?.target?.value,
        surveyRespondentId: surveyRespondentId,
      };
      await dispatch(fetchSectionData(payload)).then((data) => {
        // Combine or merge the section data as needed
        // For simplicity, this example assumes you replace existing data

        dispatch(setSectionData(data?.payload));
      });
    }
  };

  return (
    <Grid
      sx={{
        "@media print": {
          marginLeft: "10px",
        },
      }}
    >
        {!isAutoRender && (
            <>
                <div style={{ marginBottom: "16px" }}>
                    <Typography sx={{ fontSize: 25, fontWeight: "bold" }} color="primary">
                        <b>{`Print Survey Menu`}</b>
                    </Typography>
                    <Typography color="primary">
                        The
                        <Typography display="inline" sx={{ fontWeight: "bold" }}>
                            {" Survey Printing "}
                        </Typography>
                        option provides a hardcopy of your online AHA survey. You can printout
                        the entire Survey, or select a section of the Survey.
                    </Typography>
                </div>
                <div style={{ marginBottom: "16px" }}>
                    <Typography color="primary">
                        Each report is customized for your hospital. Depending upon the type
                        of connection you have, it may take few moments to generate and
                        download. We appreciate your patience.
                    </Typography>
                </div>

                <div>
                    <Typography sx={{ fontWeight: "bold" }} mb={1}>
                        {"Select survey:"}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: "30%" }}>
                        <Select
                            id="SurveySelect"
                            value={surveyId}
                            placeholder="Please select survey"
                            onChange={(event) => handleSurveyToPrint(event)}
                        >
                            {surveyMenu?.surveys?.map((opt) => (
                                <MenuItem value={opt.id}>{getTextFromHTML(opt.name)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                {surveyId && (
                    <div my={2}>
                        <Typography sx={{ fontWeight: "bold" }} mb={1} mt={3}>
                            {"Select section:"}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: "30%" }}>
                            <Select
                                id="SurveySelect"
                                value={sectionSelection}
                                placeholder="Select section to print"
                                onChange={(event) => handleSectionSelection(event)}
                            >
                                {sectionOptions.map((opt) => (
                                    <MenuItem value={opt.value}>
                                        {getTextFromHTML(opt.label)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}
            </>
        )}
        {!isAutoRender && sectionSelection && surveyData && (
        <div
          style={{
            width: "100%",
            textAlign: "start",
            paddingRight: "15px",
            paddingTop: "10px",
          }}
        >
          <div className="inner-box inner-box2">
            <Button
              sx={{ mr: 1 }}
              variant="contained"
              color={"primary"}
              onClick={() => handlePrint()}
            >
              Print
            </Button>
            <Button
              sx={{ mr: 1 }}
              variant="contained"
              color={"primary"}
              onClick={() => handleBack()}
            >
              Back
            </Button>
          </div>
        </div>
      )}
        {sectionsLoading ? (
        <Box
          display="flex"
          sx={{ height: "100%", width: "100%" }}
          bgcolor="#d9e0ed"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <form ref={componentRef} ml={2}>
          {surveyData && sectionSelection && (
            <>
              <div style={{ marginBottom: "16px", marginTop: "50px" }}>
                <Typography color="primary" sx={{
                  '@media print': {
                    display: 'block !important',
                    visibility: 'visible !important',
                    pageBreakInside: 'avoid'
                  }
                }}>
                  <b>{`${HospitalName} (${ahaId})`}</b>
                  <br />
                </Typography>
              </div>
              <div className="title">
                <Typography
                  sx={{
                    fontSize: 25,
                    fontWeight: "bold",
                    '@media print': {
                      display: 'block !important',
                      visibility: 'visible !important',
                      pageBreakInside: 'avoid'
                    }
                  }}
                  color="primary"
                >
                  {getTextFromHTML(
                    surveyMenu?.surveys?.find(
                      (survey) => survey.id === surveyId
                    )?.name
                  )}
                </Typography>
              </div>
            </>
          )}
          {surveyData &&
            sectionSelection &&
            surveyData.map((section) => (
              <>
                <div className="title">
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: "bold",
                      paddingBottom: "5px",
                    }}
                    color="primary"
                  >
                    {getTextFromHTML(section?.name)}
                  </Typography>
                </div>
                {Object.values(section?.pages).map((page) => (
                  <>
                    <div className="title">
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: "bold",
                          paddingBottom: "5px",
                        }}
                        color="primary"
                      >
                        {htmlParser(page?.page?.name)}
                      </Typography>
                    </div>
                    {page?.questionGroups.map((qes) => (
                      <>
                        {qes.groupType.name === "pdfpagebreakgrp" && (
                          <div style={{ pageBreakBefore: "always", height: "1px" }} />
                        )}
                        {qes.groupType.name === "radiogrp" && (
                          <RenderRadioGrp type="preview" questionsGrp={qes} />
                        )}
                        {qes.groupType.name === "checkboxgrp" && (
                          <RenderCheckboxGrp
                            type="preview"
                            questionsGrp={qes}
                          />
                        )}
                        {qes.groupType.name === "commongrp" && (
                          <RenderCommonGrp type="preview" questionsGrp={qes} />
                        )}
                        {qes.groupType.name !== "radiogrp" &&
                          qes.groupType.name !== "checkboxgrp" &&
                          qes.groupType.name !== "commongrp" &&
                          qes.questions &&
                          qes.questions.length > 0 &&
                          qes.questions.length === 1 &&
                          qes.questions.map((question) => {
                            return (
                              <RenderQuestion
                                type="preview"
                                question={question}
                                multiple={false}
                              />
                            );
                          })}
                        {qes.groupType.name !== "radiogrp" &&
                          qes.groupType.name !== "checkboxgrp" &&
                          qes.groupType.name !== "commongrp" &&
                          qes.questions?.length > 1 && (
                            <>
                              <StyledEngineProvider injectFirst>
                                <Card className="card">
                                  <CardContent className="card-header">
                                    <Grid container direction={"column"}>
                                      <Grid
                                        item
                                        alignSelf="start"
                                        className="question-container"
                                      >
                                        <Typography variant="question">
                                          {htmlParser(
                                            qes.descriptionPosition !== null ||
                                              qes.descriptionPosition !==
                                                "No Title"
                                              ? (qes.label || "") +
                                                  " " +
                                                  (qes.name || "")
                                              : (qes.label || "") +
                                                  " " +
                                                  (qes.description || "")
                                          )}
                                          {qes?.helpText && (
                                            <HelpTextPopper
                                              question={qes}
                                            ></HelpTextPopper>
                                          )}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <div>
                                      <Typography variant="h6">
                                        {htmlParser(
                                          qes.descriptionPosition !== null ||
                                            qes.descriptionPosition !==
                                              "No Title"
                                            ? qes.description || ""
                                            : ""
                                        )}
                                      </Typography>
                                    </div>
                                  </CardContent>
                                  <CardContent
                                    className="card-content"
                                    sx={{ backgroundColor: "#fff !important" }}
                                  >
                                    {qes.questions.map((question) => {
                                      return (
                                        <RenderQuestion
                                          type="preview"
                                          question={question}
                                          multiple={true}
                                        />
                                      );
                                    })}
                                  </CardContent>
                                </Card>
                              </StyledEngineProvider>
                            </>
                          )}
                      </>
                    ))}
                  </>
                ))}
                <div style={{ pageBreakAfter: "always", height: "1px" }}></div>
              </>
            ))}

          {!isAutoRender && (
            <div
              class="hideBtn"
              style={{
                width: "100%",
                textAlign: "start",
                paddingRight: "15px",
                paddingTop: "10px",
              }}
            >
              <div className="inner-box inner-box2">
                <Button
                  sx={{ mr: 1 }}
                  variant="contained"
                  color={"primary"}
                  onClick={() => handleBack()}
                >
                  Back
                </Button>
              </div>
            </div>
          )}

            {allSectionsLoaded && (
                <div id="pdf-ready-marker" style={{ display: 'none' }}>
                    All sections loaded
                </div>
            )}
        </form>
      )}
    </Grid>
  );
};

export default PrintSurvey;
